import React, { useState, useEffect, useContext, useCallback } from "react";
import { ModalBox, ModalInner, TitleText } from "./onboarding_styles";
import { AddOrg } from "./add_org";
import { useAuth } from "../../services/use-auth";
import InitialImportWorkflow from "../../utilities/mass_import/onboard_import_workflow";
import UserContext from "../../assets/user_context";
import { Modal, Box, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChooseAPath from "./choose_a_path";
import StartingBalancesOnboarding from "./starting_balances_onboarding";
import { CenteringDiv } from "../../assets/containers";
import { Close } from "@mui/icons-material";
import { doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import SignOutModal from "../../nav/signOut";
import { llmOnboardingEnabled } from "./llm/featureGate";

const Onboarding = React.forwardRef(
  ({ closeModal, openOnboardingModal, closeOnboardingModal }, ref) => {
    const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(undefined);
    const [showSignOutModal, setShowSignOutModal] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const {
      org,
      userData,
      user,
      accounts,
      funds,
      contacts,
      transactions,
      accountsHierarchy,
      orgData,
      openSubscribe,
      setOpenSubscribe,
      loaded,
      inviteCheckState,
    } = useContext(UserContext);
    const authHook = useAuth();
    const db = authHook.db;

    const orgIsOnboarded = orgData?.onboarded;
    const isOnWelcomePage =
      window.location.pathname === "/account/welcome-to-the-club";
    const chosenPath = orgData?.chosenPath;
    const userHasOrgs = userData?.orgs?.length > 0;

    const openOnboarding = useCallback(() => {
      if (!openSubscribe) {
        setOnboardingModalOpen(true);
      }
    }, [openSubscribe]);

    const closeOnboarding = () => {
      setOnboardingModalOpen(false);
    };

    // Close modal immediately if org is onboarded
    useEffect(() => {
      if (orgIsOnboarded) {
        console.log("ORG IS ONBOARDED - closing onboarding modal");
        closeOnboarding();
      }
    }, [orgIsOnboarded]);

    // Remove automatic subscription prompt - now handled by ButtonWithSubscription and SubscriptionBanner

    // Main onboarding logic - only runs when we have all the data we need
    useEffect(() => {
      // Don't do anything until we have loaded data
      if (!loaded) {
        console.log("Data not loaded yet, waiting...");
        return;
      }

      // If no user, don't show onboarding
      if (!user) {
        console.log("No user logged in, not showing onboarding");
        closeOnboarding();
        return;
      }

      // If org is already onboarded, don't show onboarding
      if (orgIsOnboarded) {
        console.log("Org is onboarded, not showing onboarding");
        closeOnboarding();
        return;
      }

      // Don't show onboarding on sign in/up pages UNLESS user has no orgs
      // (we now show the modal immediately and let it handle invite checking)
      const isOnAuthPage = ["/signin", "/signup"].includes(
        window.location.pathname,
      );
      const needsOrgCreation = !userHasOrgs;

      if (isOnAuthPage && !needsOrgCreation) {
        console.log(
          "On sign in/up page and user doesn't need org creation, not showing onboarding",
        );
        return;
      }

      // If user has no orgs, show add org modal immediately
      if (!userHasOrgs) {
        console.log(
          "User has no orgs, showing add org onboarding with invite check status...",
        );
        console.log("Invite check state:", inviteCheckState);

        // If invites were found and processed (user now has orgs), close the modal
        if (inviteCheckState?.found && userHasOrgs) {
          console.log("User was added to org via invite, closing modal");
          closeOnboarding();
          return;
        }

        // Show add org modal immediately - it will handle the invite checking UI internally
        // Add a small delay if we're on the signup page to allow for potential redirects
        if (isOnAuthPage) {
          console.log("On auth page, adding small delay before showing modal");
          setTimeout(() => {
            setOnboardingStep("addOrg");
            openOnboarding();
          }, 1000); // 1 second delay
        } else {
          setOnboardingStep("addOrg");
          openOnboarding();
        }
        return;
      }

      // If we have user and orgs but no org selected yet, wait
      if (!org) {
        console.log("No org selected yet, waiting...");
        closeOnboarding();
        return;
      }

      // If we don't have org data yet, wait
      if (!orgData) {
        console.log("No org data yet, waiting...");
        closeOnboarding();
        return;
      }

      // Now we know: user exists, org exists, org data exists, org is not onboarded
      console.log("Org needs onboarding, checking chosen path...");

      // LLM-guided onboarding (default in dev/qa): route to the chat instead of the legacy modal.
      if (llmOnboardingEnabled()) {
        closeOnboarding();
        if (window.location.pathname !== "/onboarding/chat") navigate("/onboarding/chat");
        return;
      }

      if (chosenPath) {
        // User has chosen a path
        if (chosenPath === "power") {
          setOnboardingStep("importInitialData");
          openOnboarding();
        } else if (chosenPath === "power-startingBalance") {
          setOnboardingStep("startingBalances");
          openOnboarding();
        } else if (chosenPath === "expert") {
          // Expert path doesn't need onboarding UI
          closeOnboarding();
        }
      } else {
        // No path chosen yet, show path selection
        setOnboardingStep("chooseYourPath");
        openOnboarding();
      }
    }, [
      loaded,
      user,
      userHasOrgs,
      org,
      orgData,
      orgIsOnboarded,
      chosenPath,
      openOnboarding,
      inviteCheckState,
    ]);

    // Don't render anything if org is onboarded
    if (orgIsOnboarded) {
      return null;
    }

    // Don't render anything if we don't have the basic data yet
    if (!loaded || !user) {
      return null;
    }

    return (
      <>
        <Modal
          open={onboardingModalOpen}
          onClose={() => alert("Please complete onboarding")}
          data-testid="onboarding-modal">
          {onboardingStep === "addOrg" ? (
            // AddOrg handles its own styling with Paper component - no need for ModalBox wrapper
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <IconButton
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.common.black,
                  zIndex: 1000,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.2)",
                  },
                }}
                onClick={() => setShowSignOutModal(true)}
                size="large"
                data-testid="onboarding-button-close">
                <Close fontSize="large" />
              </IconButton>
              <AddOrg
                db={db}
                userData={userData}
                user={user}
                closeModal={closeOnboarding}
                inviteCheckState={inviteCheckState}
              />
            </Box>
          ) : (
            // Other onboarding steps use the legacy dark modal styling
            <ModalBox ref={ref} tabIndex={-1}>
              <ModalInner>
                {!accounts?.length > 0 &&
                  !contacts?.length > 0 &&
                  !transactions?.length > 0 &&
                  chosenPath === "power" &&
                  loaded && (
                    <Close
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        color: "white",
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        await updateDoc(
                          doc(db, "orgs", org),
                          {
                            chosenPath: false,
                          },
                          { merge: true },
                        );
                      }}
                      fontSize="large"
                    />
                  )}
                {onboardingStep === "importInitialData" && (
                  <InitialImportWorkflow
                    initial
                    db={db}
                    org={org}
                    accounts={accounts}
                    funds={funds}
                    accountsHierarchy={accountsHierarchy}
                    contacts={contacts}
                  />
                )}
                {onboardingStep === "chooseYourPath" && <ChooseAPath />}
                {onboardingStep === "startingBalances" &&
                  accounts.length > 0 &&
                  transactions.length > 0 && <StartingBalancesOnboarding />}
                {onboardingStep === "confirmTransactions" && (
                  <CenteringDiv>
                    <TitleText>{`You already have ${transactions.length} transactions imported.`}</TitleText>
                  </CenteringDiv>
                )}
              </ModalInner>
            </ModalBox>
          )}
        </Modal>
        <SignOutModal
          open={showSignOutModal}
          handleClose={() => setShowSignOutModal(false)}
        />
      </>
    );
  },
);

export default Onboarding;
